/**
 * Estimation-exclusion settings — ``/settings/estimation-exclusions``.
 *
 * A time range the operator wants to omit from the forecast basis (bar
 * closed, party night with abnormal consumption). Soft-delete via DELETE.
 *
 * Source semantics:
 *
 * - ``manual`` — default; any operator-curated exclusion.
 * - ``festival`` — the festival itself. At most one active row may carry
 *   this source; the server enforces the singleton invariant and
 *   returns 409 if violated. The estimator uses this row's ``from_at``
 *   as the default Must-last-until on the estimation page (= "stock
 *   should last until the festival starts, when strichliste goes
 *   offline").
 *
 * Also linked from the estimation page (the "manage exclusion ranges"
 * link there points here).
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";

import {
  ApiError,
  type EstimationExclusionRow,
  api,
} from "../../api/client";
import { formatCompactDateTime } from "../../utils/dateFormat";

const { Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;

interface ExclusionFormValues {
  range: [Dayjs, Dayjs];
  label?: string | null;
  source: "manual" | "festival";
}

export const EstimationExclusionsPage = () => {
  const [rows, setRows] = useState<EstimationExclusionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EstimationExclusionRow | null>(null);
  const [form] = Form.useForm<ExclusionFormValues>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.listEstimationExclusions());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ source: "manual" });
    setModalOpen(true);
  };

  const openEdit = (row: EstimationExclusionRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      range: [dayjs(row.from_at), dayjs(row.to_at)],
      label: row.label ?? "",
      source: row.source,
    });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: ExclusionFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const [from, to] = values.range;
    const payload = {
      from_at: from.toISOString(),
      to_at: to.toISOString(),
      label: values.label ? values.label : null,
      source: values.source,
    };
    try {
      if (editing) {
        await api.patchEstimationExclusion(
          editing.id,
          payload,
          editing.updated_at,
        );
      } else {
        await api.createEstimationExclusion(payload);
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onDelete = async (row: EstimationExclusionRow) => {
    try {
      await api.deleteEstimationExclusion(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const columns: ColumnsType<EstimationExclusionRow> = [
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 120,
      sorter: (a, b) => a.source.localeCompare(b.source),
      render: (v: EstimationExclusionRow["source"]) =>
        v === "festival" ? (
          <Tag color="magenta">Festival</Tag>
        ) : (
          <Tag>Manual</Tag>
        ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      sorter: (a, b) => (a.label ?? "").localeCompare(b.label ?? ""),
      render: (v: string | null) => v ?? <em>—</em>,
    },
    {
      title: "From",
      dataIndex: "from_at",
      key: "from_at",
      sorter: (a, b) => a.from_at.localeCompare(b.from_at),
      defaultSortOrder: "descend",
      render: (v: string) => formatCompactDateTime(v),
    },
    {
      title: "To",
      dataIndex: "to_at",
      key: "to_at",
      sorter: (a, b) => a.to_at.localeCompare(b.to_at),
      render: (v: string) => formatCompactDateTime(v),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_: unknown, row) => (
        <Space size={4}>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this exclusion range?"
            description="Soft-delete — kept for audit."
            onConfirm={() => void onDelete(row)}
          >
            <Button danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Estimation exclusion ranges
        </Title>
        <Button type="primary" onClick={openCreate}>
          + New exclusion range
        </Button>
      </Space>
      <Paragraph type="secondary">
        Time spans omitted from the order-estimation forecast basis. Useful
        when the bar was closed or the venue had abnormal consumption
        (party night). Mark the festival itself with the
        {" "}<Tag color="magenta" style={{ marginRight: 0 }}>Festival</Tag>{" "}
        source — its {`"from"`} date seeds the default Must-last-until on
        the estimation page (= when strichliste goes offline).
      </Paragraph>
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Table<EstimationExclusionRow>
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
      <Modal
        title={editing ? "Edit exclusion range" : "New exclusion range"}
        open={modalOpen}
        onOk={() => void onSubmit()}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        okText={editing ? "Save" : "Create"}
        destroyOnHidden
      >
        <Form<ExclusionFormValues> form={form} layout="vertical">
          <Form.Item
            label="Source"
            name="source"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: "manual", label: "Manual" },
                {
                  value: "festival",
                  label: "Festival (only one allowed)",
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Range"
            name="range"
            rules={[
              { required: true, message: "Range is required" },
              {
                validator: (_, v: [Dayjs, Dayjs] | undefined) =>
                  v && v[0] && v[1] && v[1].isAfter(v[0])
                    ? Promise.resolve()
                    : Promise.reject(new Error("end must be after start")),
              },
            ]}
          >
            <RangePicker
              showTime
              format="DD.MM.YYYY HH:mm"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label="Label (optional)" name="label">
            <Input maxLength={120} placeholder="e.g. Mid-summer party" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default EstimationExclusionsPage;
